import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMouseDownOutside } from "../hooks/useMouseDownOutside";
import { usePersonalWorkEnabled } from "../hooks/usePersonalWorkEnabled";
import { navigationShortcuts } from "../lib/navigationShortcuts";
import { zIndexes } from "../lib/zIndexes";
import { Button } from "./Button";
import { Icon, type IconName } from "./Icon";
import { Keycap } from "./Keycap";
import { SearchInput } from "./SearchField";
import { Text } from "./Text";
import { sidePanelStyles } from "./sidePanelStyles";
import { stepTabShortcuts } from "./stepTab";

type Entry = { description: string; keys: string[]; personalWork?: true };
type Group = { label: string; entries: Entry[] };

const GROUPS: Group[] = [
  {
    label: "Global",
    entries: [
      { keys: ["cmd", "k"], description: "Open search" },
      { keys: ["cmd", "j"], description: "Open 3B activity" },
      { keys: ["cmd", "/", "or", "?"], description: "Open shortcuts and tips" },
      { keys: ["cmd", "\\"], description: "Show/hide navigation" },
      { keys: ["cmd", "shift", "\\"], description: "Show/hide sidepanel" },
      { keys: ["shift", "option", "d"], description: "Toggle light/dark mode" },
    ],
  },
  {
    label: "Go to",
    entries: navigationShortcuts.map((shortcut) => ({
      keys: ["g", "then", shortcut.key],
      description: shortcut.label,
      ...("personalWork" in shortcut ? { personalWork: true as const } : {}),
    })),
  },
  {
    label: "Search",
    entries: [
      { keys: ["arrow-up", "arrow-down"], description: "Move between results" },
      { keys: ["enter"], description: "Open selected result" },
      { keys: ["esc"], description: "Close search" },
    ],
  },
  {
    label: "Chat",
    entries: [
      {
        keys: ["shift", "option", "n"],
        description: "New chat (in Chats)",
        personalWork: true,
      },
      { keys: ["/"], description: "Focus chat or search" },
      { keys: ["esc"], description: "Unfocus chat" },
      { keys: ["cmd", "enter"], description: "Force send (interrupt reply)" },
      { keys: ["enter"], description: "Send or queue message" },
      { keys: ["shift", "enter"], description: "New line" },

      { keys: ["type", "/"], description: "Open AI suggestions" },
      {
        keys: ["shift", "click"],
        description: "Skill picker: open skill",
      },
      {
        keys: ["shift", "enter"],
        description: "Skill picker: open highlighted skill",
      },
      { keys: ["type", "@"], description: "Mention a step or connector" },
      { keys: ["cmd", "u"], description: "Add files" },
      { keys: ["drag", "or", "paste"], description: "Attach files" },
      { keys: ["highlight"], description: "Add selected code as context" },
      { keys: ["right-click"], description: "Attachment: more actions" },
      { keys: ["cmd", "shift", "."], description: "Toggle composer controls" },
      { keys: ["shift", "option", "v"], description: "Start voice mode" },
      {
        keys: ["hold", "right-option"],
        description: "Voice mode while held",
      },
    ],
  },
  {
    label: "Workflow chat",
    entries: [
      { keys: ["shift", "option", "p"], description: "Toggle Plan mode" },
      { keys: ["option", "f"], description: "Follow 3B" },
    ],
  },
  {
    label: "Message queue (empty composer)",
    entries: [
      {
        keys: ["arrow-up", "arrow-down"],
        description: "Select a queued message",
      },
      {
        keys: ["arrow-left", "arrow-right"],
        description: "Move across cancel, edit, send",
      },
      { keys: ["enter"], description: "Run the selected action" },
      { keys: ["esc"], description: "Deselect" },
    ],
  },
  {
    label: "Chat prompts",
    entries: [
      { keys: ["1"], description: "Approve plan" },
      { keys: ["2"], description: "Keep planning" },
      { keys: ["1-9"], description: "Pick an option" },
      { keys: ["arrow-up", "arrow-down"], description: "Move between options" },
      { keys: ["space"], description: "Toggle option (multi-select)" },
      {
        keys: ["ctrl", "cmd", "arrow-left", "arrow-right"],
        description: "Move between questions",
      },
      { keys: ["shift", "esc"], description: "Type a custom answer" },
      {
        keys: ["shift", "enter"],
        description: "Connector prompt: open instead",
      },
      { keys: ["esc"], description: "Dismiss" },
    ],
  },
  {
    label: "New workflow",
    entries: [{ keys: ["tab"], description: "Accept the suggested prompt" }],
  },
  {
    label: "3B activity",
    entries: [
      { keys: ["arrow-up", "arrow-down"], description: "Move between items" },
      { keys: ["1-9"], description: "Jump to item" },
      { keys: ["enter"], description: "Open selected" },
      { keys: ["esc"], description: "Close" },
    ],
  },
  {
    label: "Step map",
    entries: [
      {
        keys: ["arrow-up", "arrow-down", "arrow-left", "arrow-right"],
        description: "Navigate steps",
      },
      { keys: ["drag"], description: "Pan the canvas" },
      { keys: ["esc"], description: "Deselect step" },
      {
        keys: ["right-click"],
        description: "Step: change color, copy URL, run options",
      },
    ],
  },
  {
    label: "Step editor",
    entries: [
      ...stepTabShortcuts.map((tab) => ({
        keys: ["cmd", "shift", tab.shortcut],
        description: `Show ${tab.label.toLowerCase()} tab`,
      })),
      { keys: ["shift", "option", "i"], description: "Toggle inspect mode" },
      { keys: ["shift", "click"], description: "Connector row: peek details" },
      {
        keys: ["right-click"],
        description: "Connector icon: manage, remove from step",
      },
    ],
  },
  {
    label: "Code view",
    entries: [
      { keys: ["cmd", "shift", "f"], description: "Find in code" },
      { keys: ["cmd", "shift", "d"], description: "Toggle diffs" },
      { keys: ["p"], description: "Previous diff" },
      { keys: ["n"], description: "Next diff" },
      {
        keys: ["shift", "option", "f"],
        description: "Pin or unpin files panel",
      },
    ],
  },
  {
    label: "Data view",
    entries: [
      {
        keys: ["option", "arrow-up", "arrow-down"],
        description: "Move between executions (panel open)",
      },
      {
        keys: ["arrow-up", "arrow-down"],
        description: "Move between executions (list focused)",
      },
      { keys: ["option", "cmd", "1"], description: "Output tab" },
      { keys: ["option", "cmd", "2"], description: "Input tab" },
      { keys: ["option", "cmd", "3"], description: "Logs tab" },
      { keys: ["option", "cmd", "4"], description: "Metadata tab" },
      { keys: ["option", "cmd", "5"], description: "Requests tab" },
      {
        keys: ["shift", "option", "e"],
        description: "Pin or unpin executions panel",
      },
      {
        keys: ["shift", "click"],
        description: "Expand or collapse all JSON keys",
      },
    ],
  },
  {
    label: "File browser",
    entries: [
      { keys: ["arrow-up", "arrow-down"], description: "Move within column" },
      {
        keys: ["arrow-right", "or", "enter"],
        description: "Open or next column",
      },
      { keys: ["arrow-left"], description: "Back to parent column" },
      { keys: ["space"], description: "Toggle preview" },
      { keys: ["esc"], description: "Close browser" },
      { keys: ["right-click"], description: "Item: open, download, more" },
    ],
  },
  {
    label: "Access graph",
    entries: [
      {
        keys: ["arrow-up", "arrow-down", "arrow-left", "arrow-right"],
        description: "Move between nodes",
      },
      { keys: ["space"], description: "Open selected node" },
      { keys: ["h"], description: "Toggle hidden" },
      { keys: ["i"], description: "Toggle inherited" },
      { keys: ["esc"], description: "Deselect" },
    ],
  },
  {
    label: "More tips",
    entries: [
      {
        keys: ["double-click"],
        description: "Reset a resized panel to default",
      },
      {
        keys: ["option", "click"],
        description: "Edit a workflow’s badge icon",
      },
      {
        keys: ["cmd", "click"],
        description: "Browser address: open in new tab",
      },
      {
        keys: ["shift", "click"],
        description: "Range-select rows in the access table",
      },
    ],
  },
];

const Container = styled("div")`
  ${sidePanelStyles}
  position: fixed;
  top: 12px;
  right: 12px;
  bottom: 12px;
  width: min(320px, calc(100vw / var(--ui-zoom, 1) - 24px));
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  z-index: ${zIndexes.keyboardShortcuts};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10.5px 12px 10px;
  box-shadow: 0 0.5px 0 var(--border-alpha);
  flex-shrink: 0;
`;

const ScrollArea = styled("div")`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled("div")`
  display: flex;
  flex-direction: column;
`;

const SectionHeading = styled("h4")`
  margin: 0;
  padding: 0 0 6px;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
`;

const Keys = styled("div")`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
`;

const EmptyState = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
`;

const ARROW_ICONS: Record<string, IconName> = {
  "arrow-up": "arrow-up",
  "arrow-down": "arrow-down",
  "arrow-left": "arrow-left",
  "arrow-right": "arrow-right",
  enter: "corner-down-left",
};

const SYMBOLS: Record<string, string> = {
  cmd: "⌘",
  shift: "⇧",
  option: "⌥",
  "right-option": "Right ⌥",
  ctrl: "⌃",
};

const CONNECTING_WORDS = new Map<string, string>([
  ["then", "then"],
  ["or", "or"],
  ["type", "Type"],
  ["hold", "Hold"],
]);

function capitalize(token: string): string {
  return token.charAt(0).toUpperCase() + token.slice(1);
}

function KeyToken({ token }: { token: string }) {
  const iconName = ARROW_ICONS[token];
  if (iconName) {
    return (
      <Keycap>
        <Icon name={iconName} size={12} />
      </Keycap>
    );
  }
  const display =
    SYMBOLS[token] ??
    (token.length === 1 ? token.toUpperCase() : capitalize(token));
  return (
    <Keycap style={{ padding: "2px 4px" }}>
      <Text variant="text-smaller-md">{display}</Text>
    </Keycap>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
};

export function KeyboardShortcutsPanel({ open, onClose }: Props) {
  const personalWorkEnabled = usePersonalWorkEnabled();
  const [containerRef] = useMouseDownOutside(() => {
    if (open) onClose();
  });
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Clear the query when the panel opens; focus is driven separately below since
  // it needs the input mounted.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setQuery("");
  }
  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose]);

  const q = query.trim().toLowerCase();
  const groups = GROUPS.map((group) => ({
    ...group,
    entries: group.entries.filter(
      (entry) =>
        (personalWorkEnabled || !entry.personalWork) &&
        (!q ||
          group.label.toLowerCase().includes(q) ||
          entry.description.toLowerCase().includes(q) ||
          entry.keys.some((token) => token.toLowerCase().includes(q)))
    ),
  })).filter((group) => group.entries.length > 0);

  if (!open) return null;

  return createPortal(
    <Container ref={containerRef} role="dialog" aria-label="Keyboard shortcuts">
      <HeaderRow>
        <Icon name="search" size={16} color="var(--text-primary)" />
        <SearchInput
          ref={inputRef}
          type="text"
          aria-label="Search shortcuts and tips"
          style={{ minWidth: 0 }}
          placeholder="Search shortcuts and tips…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button
          variant="secondary"
          icon="x"
          aria-label="Close keyboard shortcuts"
          onClick={onClose}
        />
      </HeaderRow>
      <ScrollArea>
        {groups.map((group) => (
          <Section key={group.label}>
            <SectionHeading>
              <Text variant="text-small" color="var(--text-secondary)">
                {group.label}
              </Text>
            </SectionHeading>
            {group.entries.map((entry) => (
              <Row key={entry.description + entry.keys.join("+")}>
                <Text ellipsis>{entry.description}</Text>
                <Keys>
                  {entry.keys.map((token, index) => {
                    const key = `${token}-${index}`;
                    const word = CONNECTING_WORDS.get(token);
                    if (word) {
                      return (
                        <Text
                          key={key}
                          variant="text-smaller-md"
                          color="var(--text-secondary)"
                        >
                          {word}
                        </Text>
                      );
                    }
                    return <KeyToken key={key} token={token} />;
                  })}
                </Keys>
              </Row>
            ))}
          </Section>
        ))}
        {groups.length === 0 && (
          <EmptyState>
            <Icon name="text-search" size={24} color="var(--text-secondary)" />
            <Text variant="text-small" color="var(--text-secondary)">
              No matching shortcuts
            </Text>
          </EmptyState>
        )}
      </ScrollArea>
    </Container>,
    document.body
  );
}
